import { Fragment, useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { getDecisionDataSigned } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getKnmStartDate } from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { getPmStartDate } from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';

import { IDecisionFormRef } from '../../../DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ISubmittedDocumentsStatusTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ISubmittedDocumentsStatusTypeSelectOption';
import { IKnmErknmEventSubmittedDocumentsForm } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmEventSubmittedDocumentsForm';
import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { SelectInputFieldAutoOptions } from '../../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { SubmittedFiles } from '../../../KnmErknmPackage/ErknmSubjectModule/components/SubmitedFiles/SubmittedFiles';
import styles from './KnmEventsDocuments.module.scss';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmDataSigned } from '../../MainModule/selectors/knmSelectors';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IKnmEventsDocumentItemProps {
  baseId: string | undefined;
  docFieldName: string;
  eventFieldName: string;
  fieldValueDocument: IKnmErknmEventSubmittedDocumentsForm;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isDecision: boolean | undefined;
  isFilledFromLink: boolean;
  isKnm: boolean | undefined;
  isPm: boolean | undefined;
  isSigned: boolean | null | undefined;
  isSignedDecision: boolean | null | undefined;
  isSubmissionWatchOnly: boolean;
  isWatchOnly: boolean;
  jIndex: number;
  sIndex: number;
  startDateEvent: string | undefined;
  startDateKnm: string | undefined;
  startDatePm: string | undefined;
  statusId: number | undefined;
  stopDateEvent: string | undefined;
}

export const KnmEventsDocumentItemWithoutStore = ({
  baseId,
  docFieldName,
  eventFieldName,
  fieldValueDocument,
  formRef,
  isDecision,
  isFilledFromLink,
  isKnm,
  isPm,
  isSigned,
  isSignedDecision,
  isSubmissionWatchOnly,
  isWatchOnly,
  jIndex,
  sIndex,
  startDateEvent,
  startDateKnm,
  startDatePm,
  statusId,
  stopDateEvent,
}: IKnmEventsDocumentItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const remove = formRef?.current.mutators.remove;
  const baseFieldId = `${baseId}.submittedDocuments`;
  // const [isTrigger, setTrigger] = useState(true);

  /**
   * Функция фильтрации значений со списком статусов представления документов
   */
  const submittedDocumentsTransformFunc = useCallback(
    (value: IKnmErknmEventSubmittedDocumentsForm) => (items: ISubmittedDocumentsStatusTypeSelectOption[]) => {
      if (!items) return [];

      if (value.files && value.files.length > 0) {
        return items.filter(i => i.id !== 2);
      }

      return items;
    },
    [],
  );

  const isNotSubmitDoc = (id?: number): boolean => {
    return !!id && [4].includes(id);
  };

  const hasError = checkHasErrorInCollapseBlock(formRef, docFieldName);

  const parseStartDateKnm = parseDate({ date: startDateKnm || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const parseStartDatePm = parseDate({ date: startDatePm || '', parseFormat: DATE_FORMAT_LONG_FNS });

  const parseStartDateEvent = parseDate({ date: startDateEvent || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const parseStopDateEvent = parseDate({ date: stopDateEvent || '', parseFormat: DATE_FORMAT_LONG_FNS });

  const parseStartKnm = isKnm ? parseStartDateKnm : parseStartDatePm;

  // Даты запроса скопированного из связи документа доступны, если нет общей блокировки документов
  const isRequestDatesWatchOnly = isSubmissionWatchOnly || (isWatchOnly && !isFilledFromLink);

  return (
    <KnmCollapse
      id={`${baseFieldId}[${jIndex}]`}
      key={`${sIndex}-${jIndex}`}
      theme="fields"
      title={`Документ №${jIndex + 1}`}
      onClose={
        !fieldValueDocument?.isNew ? undefined : () => remove && remove(`${eventFieldName}.submittedDocuments`, jIndex)
      }
      // onToggleCallback={() => setTrigger(state => !state)}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
      opened={fieldValueDocument?.isNew}
    >
      <Fragment>
        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: 'Наименование документа',
              field: `${docFieldName}.documentTitle`,
              isDecision,
              isKnm,
              isPm,
            })}
            color="black"
            id={`${baseFieldId}[${jIndex}].documentTitle`}
          >
            <TextInputField
              id={`${docFieldName}.documentTitle`}
              name={`${docFieldName}.documentTitle`}
              placeholder="Введите наименование"
              disabled={isWatchOnly}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Описание" color="black" id={`${baseFieldId}[${jIndex}].description`}>
            <ReactQuillEditorField
              id={`${docFieldName}.descriptionReactQuillEditor`}
              name={`${docFieldName}.descriptionReactQuillEditor`}
              placeholder="Введите описание"
              disabled={isWatchOnly}
              handleCustomChange={(e: string) => {
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formRef?.current?.change?.(`${docFieldName}.description` as any, e);
              }}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: 'Дата и время запроса',
              field: `${docFieldName}.requestDateTime`,
              isDecision,
              isKnm,
              isPm,
            })}
            color="black"
            size="half"
            placementLabel="left"
            id={`${baseFieldId}[${jIndex}].requestDateTime`}
          >
            <DatePickerField
              id={`${docFieldName}.requestDateTime`}
              name={`${docFieldName}.requestDateTime`}
              disabled={isRequestDatesWatchOnly}
              timeFormat
            />
          </FieldBlock>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: 'Срок представления',
              field: `${docFieldName}.submissionDeadline`,
              isDecision,
              isKnm,
              isPm,
            })}
            color="black"
            size="half"
            placementLabel="left"
            id={`${baseFieldId}[${jIndex}].submissionDeadline`}
          >
            <DatePickerField
              id={`${docFieldName}.submissionDeadline`}
              name={`${docFieldName}.submissionDeadline`}
              disabled={isRequestDatesWatchOnly}
              minDate={parseStartDateEvent || parseStartKnm}
              maxDate={parseStopDateEvent}
            />
          </FieldBlock>
        </Row>
        {(isSigned || isSignedDecision) && (
          <Row>
            <FieldBlock
              label="Статус представления"
              color="black"
              size="half"
              placementLabel="left"
              id={`${baseFieldId}[${jIndex}].status`}
            >
              <SelectInputFieldAutoOptions
                id={`${docFieldName}.status`}
                name={`${docFieldName}.status`}
                className={styles.SelectStatus}
                isClearable={false}
                size="large"
                optionName={CommonCatalogsNamesEnum.ERKNM_SUBMITTED_DOCUMENTS_STATUS}
                disabled={isSubmissionWatchOnly}
                transformFunc={submittedDocumentsTransformFunc(fieldValueDocument)}
                onChange={(val: ISubmittedDocumentsStatusTypeSelectOption | undefined) =>
                  isNotSubmitDoc(val?.id) &&
                  // @ts-ignore
                  formRef?.current.change(`${docFieldName}.submissionDateTime`)
                }
              />
            </FieldBlock>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: 'Дата и время получения документа',
                field: `${docFieldName}.submissionDateTime`,
                isDecision,
                isKnm,
                isPm,
              })}
              color="black"
              size="half"
              placementLabel="left"
              id={`${baseFieldId}[${fieldValueDocument.guid}].submissionDateTime`}
            >
              <DatePickerField
                id={`${docFieldName}.submissionDateTime`}
                name={`${docFieldName}.submissionDateTime`}
                disabled={isSubmissionWatchOnly || isNotSubmitDoc(statusId)}
                timeFormat
              />
            </FieldBlock>
          </Row>
        )}

        {!isNotSubmitDoc(statusId) && (isSigned || isSignedDecision) && (
          <Row>
            <SubmittedFiles
              name={`${docFieldName}.files`}
              id={`${baseFieldId}[${fieldValueDocument.guid}].files`}
              isWatchOnly={isSubmissionWatchOnly}
              index={jIndex}
              sIndex={sIndex}
              formRef={formRef}
              isDecision={isDecision}
              isKnm={isKnm}
              isPm={isPm}
            />
          </Row>
        )}
      </Fragment>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isSigned: getKnmDataSigned(state),
  isSignedDecision: getDecisionDataSigned(state),
  startDateKnm: getKnmStartDate(state),
  startDatePm: getPmStartDate(state),
});

export const KnmEventsDocumentItem = connect(mapStateToProps)(KnmEventsDocumentItemWithoutStore);
